import styles from "./CustomizingSection.module.scss";
import { SectionCard } from "./SectionCard";

export function CustomizingSection() {
  return (
    <SectionCard
      ariaLabelledby="customizing-h"
      title="How to Make Your Work Product Dynamic"
      description="Use placeholders to automatically adapt your work product to different cases, clients, and document types."
      id="customizing"
      className={styles.customizing}
    >
      <h3>Using Placeholders</h3>
      <p>
        Placeholders are simple words or phrases surrounded by double curly
        brackets. They must be typed <strong>exactly</strong> as displayed below
        to work.
      </p>

      <h4>Example</h4>
      <p>When you type:</p>
      <pre>
        <code>
          Responding Party objects to this Interrogatory on the ground that the
          information sought is in the possession of, known to, or otherwise
          equally available to {"{{PROPOUNDING_PARTY_TYPE}}"}.
        </code>
      </pre>
      <p>
        When the propounding party is a plaintiff, it will appear in your
        document as:
      </p>
      <pre>
        <code>
          Responding Party objects to this Interrogatory on the ground that the
          information sought is in the possession of, known to, or otherwise
          equally available to Plaintiff.
        </code>
      </pre>

      <h3>Available Placeholders</h3>
      <div style={{ overflowX: "auto" }}>
        <table>
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "38%" }} />
            <col style={{ width: "28%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Placeholder</th>
              <th>Resultant Text</th>
              <th>Applies To</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>{"{{REQUEST}}"}</code>
              </td>
              <td>Request / Interrogatory</td>
              <td>All request types</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REQUEST_TYPE}}"}</code>
              </td>
              <td>
                Requests for Admission, Requests for Production,
                Interrogatories, Form Interrogatories
              </td>
              <td>All request types</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REQUEST_TYPE_SINGULAR}}"}</code>
              </td>
              <td>
                Request for Admission / Request for Production / Interrogatory /
                Form Interrogatory
              </td>
              <td>All request types</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REQUEST_OBJECT}}"}</code>
              </td>
              <td>information / documents</td>
              <td>
                RFAs, Interrogatories, Form Interrogatories (information); RFPs
                (documents)
              </td>
            </tr>
            <tr>
              <td>
                <code>{"{{LINKING_VERB}}"}</code>
              </td>
              <td>is / are</td>
              <td>"is" for information; "are" for documents</td>
            </tr>
            <tr>
              <td>
                <code>{"{{RESPONDENT}}"}</code>
              </td>
              <td>Plaintiff / Defendant / Respondent / custom name</td>
              <td>Defined under "Refer to Responding Party As"</td>
            </tr>
            <tr>
              <td>
                <code>{"{{RESPONDING_PARTY}}"}</code>
              </td>
              <td>
                The responding party&apos;s name
              </td>
              <td>Document / case</td>
            </tr>
            <tr>
              <td>
                <code>{"{{RESPONDING_PARTY_TYPE}}"}</code>
              </td>
              <td>Plaintiff / Defendant</td>
              <td>Depends on responding party role</td>
            </tr>
            <tr>
              <td>
                <code>{"{{RESPONDING_PARTY_TYPE_POSSESSIVE}}"}</code>
              </td>
              <td>
                Responding party type with correct possessive (&apos;s or
                &apos;)
              </td>
              <td>Document / case</td>
            </tr>
            <tr>
              <td>
                <code>{"{{PROPONENT}}"}</code>
              </td>
              <td>
                Respondent name on the client record when set; otherwise
                &quot;Propounding Party&quot;
              </td>
              <td>Set in the "Refer to Client As" option on the edit client popup</td>
            </tr>
            <tr>
              <td>
                <code>{"{{PROPOUNDING_PARTY}}"}</code>
              </td>
              <td>The propounding party's name</td>
              <td>Set in the "Propounding Party" confirmation page</td>
            </tr>
            <tr>
              <td>
                <code>{"{{PROPOUNDING_PARTY_TYPE}}"}</code>
              </td>
              <td>Plaintiff / Defendant</td>
              <td>Depends on propounding party role</td>
            </tr>
            <tr>
              <td>
                <code>{"{{PROPOUNDING_PARTY_TYPE_POSSESSIVE}}"}</code>
              </td>
              <td>
                Propounding party type with correct possessive (&apos;s or
                &apos;)
              </td>
              <td>Document / case</td>
            </tr>
            <tr>
              <td>
                <code>{"{{DEFENDANT_NAME}}"}</code>
              </td>
              <td>Defendant name from case or document</td>
              <td>Document / case</td>
            </tr>
            <tr>
              <td>
                <code>{"{{PLAINTIFF_NAME}}"}</code>
              </td>
              <td>Plaintiff name from case or document</td>
              <td>Document / case</td>
            </tr>
            
            
            <tr>
              <td>
                <code>{"{{STATE}}"}</code>
              </td>
              <td>Short name of the jurisdiction as used in common language. E.g. Tennessee, California, New York</td>
              <td>Case jurisdiction</td>
            </tr>
            <tr>
              <td>
                <code>{"{{FIRM_ADDRESS}}"}</code>
              </td>
              <td>Your firm&apos;s address as stored in Briefpoint</td>
              <td>Firm settings</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REPRESENTATIVE_MEMBERS}}"}</code>
              </td>
              <td>
                Standard &quot;Representative Members,&quot; or
                class/PAGA-specific headings when the case is set up that way
              </td>
              <td>Case class / PAGA setup</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REPRESENTATIVE_PERIOD}}"}</code>
              </td>
              <td>
                Standard &quot;Representative Period,&quot; or class period /
                PAGA period labels when applicable
              </td>
              <td>Case class / PAGA setup</td>
            </tr>
            <tr>
              <td>
                <code>{"{{RESPONSIVE_DOCS}}"}</code>
              </td>
              <td>
                Bates or Exhibit ranges
              </td>
              <td>Production Responses</td>
            </tr>
            <tr>
              <td>
                <code>{"{{HAS_HAVE}}"}</code>
              </td>
              <td>has / have</td>
              <td>
                Picked based on the plurality of the preceding party reference;
                defaults to the responding party&apos;s setting if unclear
              </td>
            </tr>
            <tr>
              <td>
                <code>{"{{IS_ARE}}"}</code>
              </td>
              <td>is / are</td>
              <td>
                Picked based on the plurality of the preceding party reference;
                defaults to the responding party&apos;s setting if unclear
              </td>
            </tr>
            <tr>
              <td>
                <code>{"{{WAS_WERE}}"}</code>
              </td>
              <td>was / were</td>
              <td>
                Picked based on the plurality of the preceding party reference;
                defaults to the responding party&apos;s setting if unclear
              </td>
            </tr>
            <tr>
              <td>
                <code>{"{{DOES_DO}}"}</code>
              </td>
              <td>does / do</td>
              <td>
                Picked based on the plurality of the preceding party reference;
                defaults to the responding party&apos;s setting if unclear
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Additional Examples</h3>
      <p>
        <strong>1.</strong> When you type:
      </p>
      <pre>
        <code>
          The {"{{REQUEST_OBJECT}}"} sought {"{{LINKING_VERB}}"} subject to the
          attorney-client privilege.
        </code>
      </pre>
      <p>It appears as:</p>
      <pre>
        <code>
          {`The information sought is subject to the attorney-client privilege.
OR
The documents sought are subject to the attorney-client privilege.`}
        </code>
      </pre>

      <p>
        <strong>2.</strong> When you type:
      </p>
      <pre>
        <code>
          {"{{RESPONDENT}}"} objects to this interrogatory on the ground that
          the information sought is too remote from the subject matter of the
          action.
        </code>
      </pre>
      <p>It appears as:</p>
      <pre>
        <code>
          Mr. Jones objects to this interrogatory on the ground that the
          information sought is too remote from the subject matter of the
          action.
        </code>
      </pre>
    </SectionCard>
  );
}
